import { groupBy } from "../analytics/engine/groupBy";
import { computeTrend } from "../analytics/engine/trend";
import { computeCorrelation } from "../analytics/engine/correlation";
import { average } from "../data/statistics";

function fmt(n) {
  if (n === null || n === undefined || isNaN(n)) return "N/A";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function pickCategoricalDimension(schema) {
  const excluded = new Set([...(schema.time || []), ...(schema.ids || [])]);
  return (schema.dimensions || []).find(d => !excluded.has(d)) || null;
}

// Replaces the old hardcoded insightEngine.js/aiInsightEngine.js with real
// insights computed from the actual dataset via the analytics engine.
export function buildInsights(data, schema) {

  if (!data?.length) return [];

  const insights = [];
  const measure = schema?.measures?.[0];
  const dimension = pickCategoricalDimension(schema || {});

  // ---- TREND ----
  if (measure && schema?.time?.length) {

    const trend = computeTrend(data, {
      dateColumn: schema.time[0],
      measure,
      period: "year",
      aggregation: "sum",
    });

    if (trend.success && trend.pctChange !== null && Math.abs(trend.pctChange) >= 1) {
      insights.push({
        type: "trend",
        title: `${measure} is ${trend.direction}`,
        message: `${measure} changed ${fmt(Math.abs(trend.pctChange))}% from ${trend.series[0].period} to ${trend.series[trend.series.length - 1].period}.`,
        icon: trend.direction === "increasing" ? "📈" : trend.direction === "decreasing" ? "📉" : "➡️",
      });
    }
  }

  // ---- CATEGORY DOMINANCE ----
  if (measure && dimension) {

    const grouped = groupBy(data, dimension, measure, "sum")
      .filter(g => g.value !== null)
      .sort((a, b) => b.value - a.value);

    if (grouped.length) {

      const total = grouped.reduce((a, g) => a + g.value, 0);
      const top = grouped[0];
      const share = total ? (top.value / total) * 100 : null;

      if (share !== null && share >= 25) {
        insights.push({
          type: "pattern",
          title: `${top.group} dominates ${dimension}`,
          message: `${top.group} accounts for ${fmt(share)}% of total ${measure}.`,
          icon: "📊",
        });
      }
    }
  }

  // ---- CORRELATION between the two strongest measures ----
  if (schema?.measures?.length >= 2) {

    const [colX, colY] = schema.measures;
    const corr = computeCorrelation(data, { columnX: colX, columnY: colY });

    if (corr.success && (corr.strength === "strong" || corr.strength === "moderate")) {
      insights.push({
        type: "correlation",
        title: `${colX} and ${colY} are related`,
        message: `A ${corr.strength} ${corr.direction} correlation (${corr.coefficient}) was found between ${colX} and ${colY}.`,
        icon: "🔗",
      });
    }
  }

  // ---- OUTLIERS on the primary measure ----
  if (measure) {

    const nums = data.map(r => parseFloat(r[measure])).filter(n => !isNaN(n));

    if (nums.length > 5) {

      const avg = average(data, measure);
      const max = Math.max(...nums);

      if (avg && max > avg * 3) {
        insights.push({
          type: "warning",
          title: `Potential outliers in ${measure}`,
          message: `The highest ${measure} (${fmt(max)}) is far above the average (${fmt(avg)}).`,
          icon: "⚠️",
        });
      }
    }
  }

  // ---- MISSING VALUES ----
  const columns = schema?.columns || Object.keys(data[0] || {});

  for (const col of columns) {

    const missing = data.filter(r => r[col] === "" || r[col] === null || r[col] === undefined).length;

    if (missing > 0) {

      const percent = (missing / data.length) * 100;

      if (percent >= 5) {
        insights.push({
          type: "warning",
          title: `Missing values in ${col}`,
          message: `${fmt(percent)}% of records are missing ${col}.`,
          icon: "⚠️",
        });
      }
    }
  }

  return insights.slice(0, 6);
}
